/* AdminPlanAuthoring — schema editor for the Plan Authoring workspace (iter 97).
 *
 * Three tabs:
 *   1. Sections — list with visibility toggles + label rename
 *   2. Fields — grid with per-field visibility toggle + label + placeholder rename
 *   3. Clinical thresholds — numeric inputs for the 10 ranges
 *
 * Each save calls PUT /api/admin/plan-authoring-schema with the local
 * draft. "Reset to defaults" calls POST .../reset.
 *
 * Adding brand-new fields beyond the data model is not supported (would
 * require a backend schema migration per field). Admin can hide/rename any
 * existing field; that's the current scope.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import SubSegmentedTabs from "@/components/admin/SubSegmentedTabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowsCounterClockwise, FloppyDisk, Eye, EyeSlash, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { confirmDialog } from "@/lib/confirm";


const SECTION_HELP = {
    bite_metrics: "Initial → final overjet/overbite + total IPR. Drives the diagnostic snapshot the doctor sees.",
    pertooth:     "Per-tooth movements + attachments + precision cuts + bite ramps. The authoritative plan content.",
    protocol:     "IPR plan + attachments + elastics + planner notes. Includes the structured IPR table and auxiliaries.",
    wear:         "Aligners & wear schedule — upper/lower counts + cadence + per-stage overrides. Triggers the arch-balance CI banner and drives the est. completion date.",
    retention:    "Retainer type + free-text wear schedule + recall months. Triggers the recall-minimum CI banner.",
    deliverable:  "Plan viewer link OR walkthrough video — the actual handoff bundle the doctor reviews.",
};

const FIELD_BY_SECTION = {
    bite_metrics: ["initial_overjet_mm", "initial_overbite_mm", "final_overjet_mm", "final_overbite_mm", "total_ipr_mm"],
    protocol:     ["proposed_attachments", "ipr_entries", "proposed_elastics", "planner_notes"],
    wear:         ["aligner_count_upper", "aligner_count_lower", "estimated_duration_weeks", "change_interval_days"],
    retention:    ["retainer_type", "retention_recall_months"],
    deliverable:  ["external_link", "video_file_id"],
};

const THRESHOLD_META = {
    arch_balance_delta:         { label: "Arch balance Δ (aligners)", help: "Warn when |upper - lower| exceeds this.", min: 0, max: 100, step: 1 },
    overjet_final_min:          { label: "Final overjet — min (mm)",  help: "Final OJ < this → CI warns.",            min: 0, max: 30, step: 0.1 },
    overjet_final_max:          { label: "Final overjet — max (mm)",  help: "Final OJ > this → CI warns.",            min: 0, max: 30, step: 0.1 },
    overbite_final_min:         { label: "Final overbite — min (mm)", help: "Final OB < this → CI warns.",            min: -10, max: 30, step: 0.1 },
    overbite_final_max:         { label: "Final overbite — max (mm)", help: "Final OB > this → CI warns.",            min: -10, max: 30, step: 0.1 },
    ipr_max_per_contact:        { label: "IPR per contact — soft cap (mm)", help: "Per-row IPR > this → table row warns.", min: 0, max: 5, step: 0.05 },
    ipr_total_flag:             { label: "Total IPR — soft cap (mm)", help: "Total IPR > this → CI warns.",          min: 0, max: 50, step: 0.1 },
    wear_default_min:           { label: "Wear cadence — min (days)", help: "Default wear < this → completion warns.", min: 1, max: 365, step: 1 },
    wear_default_max:           { label: "Wear cadence — max (days)", help: "Default wear > this → completion warns.", min: 1, max: 365, step: 1 },
    retainer_recall_min_months: { label: "Retainer recall — min (months)", help: "Recall < this → CI warns.",         min: 0, max: 60, step: 1 },
};


export default function AdminPlanAuthoring({ embedded = false }) {
    const [schema, setSchema] = useState(null);
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);
    const [tab, setTab] = useState("sections");

    const load = async () => {
        try {
            const r = await api.get("/plan-authoring-schema");
            setSchema(r.data);
            setDraft(JSON.parse(JSON.stringify(r.data)));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't load schema");
        }
    };

    useEffect(() => { load(); }, []);

    if (!draft) {
        return <div className="p-6 text-sm text-muted-foreground">Loading schema…</div>;
    }

    const dirty = JSON.stringify(schema) !== JSON.stringify(draft);

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/admin/plan-authoring-schema", {
                sections: draft.sections,
                fields: draft.fields,
                clinical_thresholds: draft.clinical_thresholds,
            });
            toast.success("Schema saved — planners will see your changes on next load.");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't save");
        } finally {
            setBusy(false);
        }
    };

    const reset = async () => {
        if (!(await confirmDialog({ title: "Reset all customization to defaults?", message: "This can't be undone.", confirmText: "Reset", tone: "danger" }))) return;
        setBusy(true);
        try {
            await api.post("/admin/plan-authoring-schema/reset");
            toast.success("Reset to defaults");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't reset");
        } finally {
            setBusy(false);
        }
    };

    /* Section helpers */
    const toggleSection = (idx) => {
        setDraft((d) => {
            const next = JSON.parse(JSON.stringify(d));
            next.sections[idx].visible = !next.sections[idx].visible;
            return next;
        });
    };
    const renameSection = (idx, label) => {
        setDraft((d) => {
            const next = JSON.parse(JSON.stringify(d));
            next.sections[idx].label = label;
            return next;
        });
    };
    /* iter 97.3 — reorder via up/down arrows. Each click swaps `order` with
       the neighbour so the planner workspace re-renders in the new sequence.
       Note: we sort the `sections` array client-side too so the admin UI
       reflects the order live (otherwise we'd only re-sort on save+reload). */
    const reorderSection = (idx, dir) => {
        setDraft((d) => {
            const next = JSON.parse(JSON.stringify(d));
            const sorted = next.sections.slice().sort((a, b) => (a.order || 99) - (b.order || 99));
            const swapIdx = idx + dir;
            if (swapIdx < 0 || swapIdx >= sorted.length) return d;
            const a = sorted[idx];
            const b = sorted[swapIdx];
            const tmp = a.order;
            a.order = b.order;
            b.order = tmp;
            next.sections = sorted;
            return next;
        });
    };

    /* Field helpers */
    const toggleField = (name) => {
        setDraft((d) => {
            const next = JSON.parse(JSON.stringify(d));
            next.fields = next.fields || {};
            next.fields[name] = next.fields[name] || {};
            next.fields[name].visible = next.fields[name].visible === false ? true : false;
            return next;
        });
    };
    const updateField = (name, key, value) => {
        setDraft((d) => {
            const next = JSON.parse(JSON.stringify(d));
            next.fields = next.fields || {};
            next.fields[name] = next.fields[name] || {};
            next.fields[name][key] = value;
            return next;
        });
    };

    /* Threshold helper */
    const updateThreshold = (key, value) => {
        setDraft((d) => {
            const next = JSON.parse(JSON.stringify(d));
            next.clinical_thresholds = next.clinical_thresholds || {};
            next.clinical_thresholds[key] = value === "" ? "" : Number(value);
            return next;
        });
    };

    return (
        <div className={embedded ? "space-y-5" : "space-y-6"} data-testid="admin-plan-authoring">
            {/* Header — only when accessed directly at /admin/plan-authoring
                (legacy route). Hidden when embedded inside Service Setup
                which supplies its own header. */}
            {!embedded && (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Operations · Service Setup · Plan Templates</div>
                        <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">Plan Templates</h1>
                        <p className="text-xs text-muted-foreground mt-1">
                            Customize the section labels, field visibility, and clinical intelligence ranges on the planner&apos;s Author the Plan workspace.
                        </p>
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between gap-3 flex-wrap">
                {/* iter101.35 — SubSegmentedTabs (thin underlined variant)
                    because these are SECOND-level tabs inside the Service
                    Setup hub. Prevents visual confusion with the pill-filled
                    main-tab bar sitting above. */}
                <SubSegmentedTabs
                    testid="admin-pa-tabs"
                    tabTestidBase="admin-pa-tab"
                    active={tab}
                    onChange={setTab}
                    tabs={[
                        { id: "sections",   label: "Sections" },
                        { id: "fields",     label: "Fields" },
                        { id: "thresholds", label: "Clinical thresholds" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        onClick={reset}
                        disabled={busy}
                        data-testid="admin-pa-reset"
                    >
                        <ArrowsCounterClockwise size={13} weight="bold" className="mr-1.5" />
                        Reset to defaults
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        className="rounded-full"
                        onClick={save}
                        disabled={busy || !dirty}
                        data-testid="admin-pa-save"
                    >
                        <FloppyDisk size={13} weight="fill" className="mr-1.5" />
                        {busy ? "Saving…" : dirty ? "Save changes" : "No changes"}
                    </Button>
                </div>
            </div>

            <div>
                {/* Sections */}
                {tab === "sections" && (
                <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                        Drag-reorder with the arrows · show/hide entire sections · rename headers. The planner&apos;s Author the Plan workspace renders sections in the exact order shown here.
                    </p>
                    {(draft.sections || []).slice().sort((a, b) => (a.order || 99) - (b.order || 99)).map((s, idx, arr) => (
                        <Card key={s.id} className={s.visible === false ? "opacity-60" : ""} data-testid={`admin-pa-section-${s.id}`}>
                            <CardContent className="p-4 flex items-center gap-4">
                                <div className="flex flex-col items-center gap-1 shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => reorderSection(idx, -1)}
                                        disabled={idx === 0}
                                        className="w-7 h-7 rounded-md grid place-items-center border border-border hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                        title="Move up"
                                        data-testid={`admin-pa-section-${s.id}-up`}
                                    >
                                        <ArrowUp size={12} weight="bold" />
                                    </button>
                                    <span className="font-display font-bold text-xs text-muted-foreground tabular-nums">
                                        {idx + 1}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => reorderSection(idx, 1)}
                                        disabled={idx === arr.length - 1}
                                        className="w-7 h-7 rounded-md grid place-items-center border border-border hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                        title="Move down"
                                        data-testid={`admin-pa-section-${s.id}-down`}
                                    >
                                        <ArrowDown size={12} weight="bold" />
                                    </button>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <Label className="text-[10px] uppercase font-bold tracking-wide text-muted-foreground">Label</Label>
                                    <Input
                                        value={s.label || ""}
                                        onChange={(e) => {
                                            // we sorted+mapped, find true draft index by id
                                            const realIdx = draft.sections.findIndex((x) => x.id === s.id);
                                            if (realIdx >= 0) renameSection(realIdx, e.target.value);
                                        }}
                                        className="h-9 mt-1"
                                        data-testid={`admin-pa-section-${s.id}-label`}
                                    />
                                    <p className="text-[11px] text-muted-foreground mt-1.5">{SECTION_HELP[s.id] || ""}</p>
                                </div>
                                <div className="shrink-0 flex flex-col items-end gap-1.5">
                                    <Switch
                                        checked={s.visible !== false}
                                        onCheckedChange={() => {
                                            const realIdx = draft.sections.findIndex((x) => x.id === s.id);
                                            if (realIdx >= 0) toggleSection(realIdx);
                                        }}
                                        data-testid={`admin-pa-section-${s.id}-visible`}
                                    />
                                    <span className="text-[10px] uppercase font-bold tracking-wide text-muted-foreground">
                                        {s.visible !== false ? "Visible" : "Hidden"}
                                    </span>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
                )}

                {/* Fields */}
                {tab === "fields" && (
                <div className="space-y-5">
                    <p className="text-xs text-muted-foreground">
                        Per-field visibility + rename. Hidden fields are skipped entirely on the planner workspace + don&apos;t trigger CI warnings.
                    </p>
                    {(draft.sections || []).map((s) => {
                        const fields = FIELD_BY_SECTION[s.id] || [];
                        if (fields.length === 0) return null;
                        return (
                            <Card key={s.id} data-testid={`admin-pa-fields-section-${s.id}`}>
                                <CardHeader className="py-3">
                                    <CardTitle className="text-sm font-display font-bold">
                                        {s.label}
                                        {s.visible === false && (
                                            <span className="ml-2 text-[10px] uppercase font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">Section hidden</span>
                                        )}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-2 pt-0">
                                    {fields.map((name) => {
                                        const f = (draft.fields || {})[name] || {};
                                        const visible = f.visible !== false;
                                        return (
                                            <div key={name} className="border border-border rounded-lg p-3 flex items-center gap-3" data-testid={`admin-pa-field-${name}`}>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleField(name)}
                                                    className={"shrink-0 w-8 h-8 rounded-full grid place-items-center transition-colors " + (visible ? "bg-emerald-100 text-emerald-700" : "bg-secondary text-muted-foreground")}
                                                    data-testid={`admin-pa-field-${name}-toggle`}
                                                    title={visible ? "Hide field" : "Show field"}
                                                >
                                                    {visible ? <Eye size={14} weight="bold" /> : <EyeSlash size={14} weight="bold" />}
                                                </button>
                                                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2 min-w-0">
                                                    <div>
                                                        <Label className="text-[10px] uppercase font-bold tracking-wide text-muted-foreground">Field</Label>
                                                        <div className="text-xs font-mono text-foreground/80 truncate mt-1">{name}</div>
                                                    </div>
                                                    <div>
                                                        <Label className="text-[10px] uppercase font-bold tracking-wide text-muted-foreground">Label shown to planner</Label>
                                                        <Input
                                                            value={f.label || ""}
                                                            onChange={(e) => updateField(name, "label", e.target.value)}
                                                            className="h-8 text-xs mt-1"
                                                            placeholder="(default)"
                                                            data-testid={`admin-pa-field-${name}-label`}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
                )}

                {/* Clinical thresholds */}
                {tab === "thresholds" && (
                <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                        Numeric ranges that drive the inline Clinical Intelligence banners + completion checklist warnings. Tightening these raises the bar for what gets flagged; loosening lets more cases through without warnings.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {Object.entries(THRESHOLD_META).map(([k, meta]) => (
                            <Card key={k} data-testid={`admin-pa-threshold-${k}`}>
                                <CardContent className="p-4">
                                    <Label className="text-xs font-semibold">{meta.label}</Label>
                                    <Input
                                        type="number"
                                        min={meta.min}
                                        max={meta.max}
                                        step={meta.step}
                                        value={(draft.clinical_thresholds || {})[k] ?? ""}
                                        onChange={(e) => updateThreshold(k, e.target.value)}
                                        className="h-9 mt-1 font-mono tabular-nums"
                                        data-testid={`admin-pa-threshold-${k}-input`}
                                    />
                                    <p className="text-[11px] text-muted-foreground mt-1">{meta.help}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
                )}
            </div>
        </div>
    );
}
